import { useCallback, useRef, useState } from "react";

import { apiFetch } from "@/lib/api";

export type PurchaseOrderMegaventoryQueuedChange =
  | { type: "payment_terms"; payment_terms: string | null }
  | { type: "special_instructions"; special_instructions: string | null }
  | { type: "amount_rounding_mode"; amount_rounding_mode: string }
  | { type: "line_description"; purchase_order_item_id: number; description: string | null }
  | { type: "line_unit_price"; purchase_order_item_id: number; unit_price: number }
  | { type: "line_quantity"; purchase_order_item_id: number; quantity: number };

type QueueEntry = {
  key: string;
  change: PurchaseOrderMegaventoryQueuedChange;
  resolve: (result: PurchaseOrderMegaventoryWriteQueueResult) => void;
  reject: (error: Error) => void;
};

export type PurchaseOrderMegaventoryWriteQueueResult = {
  ok: boolean;
  changed: boolean;
  lifecycle: { reverted_to_pending: boolean; re_approved: boolean; update_count: number };
  line_rows: {
    purchase_order_item_id: number;
    description: string | null;
    unit_price: number | null;
    quantity?: number | null;
  }[];
};

export function purchaseOrderMegaventoryChangeKey(change: PurchaseOrderMegaventoryQueuedChange): string {
  if (change.type === "payment_terms") {
    return "payment_terms";
  }
  if (change.type === "special_instructions") {
    return "special_instructions";
  }
  if (change.type === "amount_rounding_mode") {
    return "amount_rounding_mode";
  }
  if (change.type === "line_unit_price") {
    return `line_unit_price:${change.purchase_order_item_id}`;
  }
  if (change.type === "line_quantity") {
    return `line_quantity:${change.purchase_order_item_id}`;
  }
  return `line_description:${change.purchase_order_item_id}`;
}

export function usePurchaseOrderMegaventoryWriteQueue(purchaseOrderId: number): {
  enqueue: (change: PurchaseOrderMegaventoryQueuedChange) => Promise<PurchaseOrderMegaventoryWriteQueueResult>;
  isQueuedOrSaving: (key: string) => boolean;
  queueDepth: number;
} {
  const queueRef = useRef<QueueEntry[]>([]);
  const drainingRef = useRef(false);
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(() => new Set());

  const markKeys = useCallback((keys: string[], mode: "add" | "remove") => {
    setPendingKeys((current) => {
      const next = new Set(current);
      for (const key of keys) {
        if (mode === "add") {
          next.add(key);
        } else {
          next.delete(key);
        }
      }
      return next;
    });
  }, []);

  const drain = useCallback(async (): Promise<void> => {
    if (drainingRef.current) {
      return;
    }
    drainingRef.current = true;
    try {
      while (queueRef.current.length > 0) {
        const batch = queueRef.current.splice(0);
        const changes = batch.map((entry) => entry.change);
        const keys = batch.map((entry) => entry.key);
        try {
          const result = await apiFetch<PurchaseOrderMegaventoryWriteQueueResult>(
            `/api/mrp/purchase-orders/${purchaseOrderId}/megaventory-changes`,
            { method: "POST", body: { changes } },
          );
          markKeys(keys, "remove");
          for (const entry of batch) {
            entry.resolve(result);
          }
        } catch (error) {
          markKeys(keys, "remove");
          const normalized = error instanceof Error ? error : new Error(String(error));
          for (const entry of batch) {
            entry.reject(normalized);
          }
        }
      }
    } finally {
      drainingRef.current = false;
      if (queueRef.current.length > 0) {
        void drain();
      }
    }
  }, [markKeys, purchaseOrderId]);

  const enqueue = useCallback(
    (change: PurchaseOrderMegaventoryQueuedChange): Promise<PurchaseOrderMegaventoryWriteQueueResult> => {
      const key = purchaseOrderMegaventoryChangeKey(change);
      markKeys([key], "add");
      return new Promise<PurchaseOrderMegaventoryWriteQueueResult>((resolve, reject) => {
        queueRef.current.push({
          key,
          change,
          resolve: (result) => {
            resolve(result);
          },
          reject,
        });
        queueMicrotask(() => {
          void drain();
        });
      });
    },
    [drain, markKeys],
  );

  const isQueuedOrSaving = useCallback(
    (key: string) => pendingKeys.has(key),
    [pendingKeys],
  );

  return {
    enqueue,
    isQueuedOrSaving,
    queueDepth: pendingKeys.size,
  };
}
